import {
  type ChangeEventHandler,
  type HTMLProps,
  type ReactNode,
  type Ref,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type DateValue, useDatePickerState } from '@react-stately/datepicker';
import type { Granularity } from '@react-types/datepicker';
import { clsx } from 'clsx';
import { type Placement, useDatePicker } from 'react-aria';
import CalendarIcon from '../../../icons/react/icon-calendar';
import { usePreferredTimeZone } from '../../../time-zone-context';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { findParentDrawer } from '../../dialog/drawer/drawer';
import { findParentModal } from '../../dialog/modal/modal';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import { DatePickerButton } from './date-picker-button';
import { DateField } from './field/date-field/date-field';
import { DatePickerPopover } from './popover/date-picker-popover';
import {
  buildChangeEvent,
  type DateAvailabilityDeterminer,
  type DatePickerActivatorRenderer,
  type DatePickerPagination,
  type DatePickerPopoverSize,
  getDateValueFromValue,
} from './util';
import inputStyles from '../input.module.scss';
import styles from './date-picker-input.module.scss';

function getPlaceholderValue(allowTime: boolean, timeZone: string, placeholder: string | undefined): DateValue | undefined {
  if (!placeholder) {
    return undefined;
  }

  try {
    return getDateValueFromValue(placeholder, allowTime, timeZone) ?? undefined;
  } catch {
    return undefined;
  }
}

export interface DatePickerInputProps extends Omit<HTMLProps<HTMLInputElement>, 'value'> {
  Activator?: DatePickerActivatorRenderer;
  allowTime?: boolean;
  calendarPlacement?: Placement;
  calendarSize?: DatePickerPopoverSize;
  dateAvailabilityDeterminer?: DateAvailabilityDeterminer;
  disabled?: boolean;
  error?: string;
  granularity?: Granularity;
  hint?: ReactNode;
  hourCycle?: 12 | 24;
  id?: string;
  label: string;
  locale?: string;
  name: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  pagination?: DatePickerPagination;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  timezone?: string;
  touched?: boolean;
  // accepts Date object, YYYY-MM-DD/ISO 8601 string (if allowTime is true), or null/undefined
  value: Date | string | null | undefined;
}

export function DatePickerInput({
  Activator,
  allowTime = false,
  calendarPlacement = 'bottom end',
  calendarSize,
  className,
  dateAvailabilityDeterminer,
  disabled,
  error,
  granularity,
  hint,
  hourCycle = 24,
  id: providedId,
  label,
  locale = 'en-US',
  name,
  onChange,
  pagination,
  placeholder,
  ref,
  required,
  timezone,
  touched,
  value,
}: DatePickerInputProps) {
  const preferredTimeZone = usePreferredTimeZone();
  const usedTimeZone = timezone ?? preferredTimeZone;
  const placeholderValue = useMemo(() => getPlaceholderValue(allowTime, usedTimeZone, placeholder), [allowTime, usedTimeZone, placeholder]);
  const [parentDialog, setParentDialog] = useState<HTMLElement | null>(null);
  const [internalValue, setInternalValue] = useState(getDateValueFromValue(value, allowTime, usedTimeZone));
  const triggerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const labelId = `${id}-label`;
  const showError = Boolean(touched && error && value);

  const state = useDatePickerState({
    granularity: granularity ?? (allowTime ? 'second' : 'day'),
    hideTimeZone: true,
    isReadOnly: disabled,
    isRequired: required,
    shouldForceLeadingZeros: true,
    isDateUnavailable: dateAvailabilityDeterminer,
    hourCycle,
    placeholderValue,
    value: internalValue,
    onChange: (newValue) => {
      setInternalValue(newValue);

      if (onChange) {
        onChange(buildChangeEvent(name, newValue, usedTimeZone, allowTime));
      }
    },
  });

  const datePickerProps = useDatePicker(
    {
      'aria-describedby': hint ? hintId : undefined,
      label,
      name,
      hourCycle,
      id,
      isDisabled: disabled,
      granularity: allowTime ? 'second' : 'day',
      shouldForceLeadingZeros: true,
      isDateUnavailable: dateAvailabilityDeterminer,
      placeholderValue,
    },
    state,
    rootRef,
  );
  const { buttonProps, calendarProps, dialogProps, fieldProps, groupProps, labelProps } = datePickerProps;

  useLayoutEffect(() => {
    setParentDialog(findParentDrawer(triggerRef.current) || findParentModal(triggerRef.current));
  }, []);

  useEffect(() => {
    setInternalValue((prevValue) => {
      const newValue = getDateValueFromValue(value, allowTime, usedTimeZone);

      if (newValue?.toString() !== prevValue?.toString()) {
        return newValue;
      }

      return prevValue;
    });
  }, [value, allowTime, usedTimeZone]);

  return (
    <VerticalStack className={clsx(inputStyles.input, styles.datePickerInput, styles.hasValue, disabled && inputStyles.isDisabled)}>
      <VerticalStack ref={triggerRef}>
        {Activator ? (
          <Activator {...datePickerProps} />
        ) : (
          <HorizontalStack
            align="space-between"
            blockAlign="center"
            className={clsx(inputStyles.field, styles.fieldContainer, styles.inputElement, className)}
            ref={rootRef}
            {...groupProps}
          >
            <Label hasError={showError} id={labelId} isFloating label={label} required={required} {...labelProps} />

            <DateField
              errorId={showError ? errorId : undefined}
              labelId={labelId}
              locale={locale}
              placeholderValue={placeholderValue}
              {...fieldProps}
              ref={ref as RefObject<HTMLDivElement>}
            />

            <HorizontalStack blockAlign="center" className={clsx(styles.affix, styles.suffix)} gap={2} wrap="nowrap">
              <DatePickerButton {...buttonProps}>
                <CalendarIcon aria-hidden height={16} />
              </DatePickerButton>
            </HorizontalStack>
          </HorizontalStack>
        )}

        {hint ? (
          <InputHint className={styles.inputElement} id={hintId}>
            {hint}
          </InputHint>
        ) : null}

        {showError ? (
          <ErrorMessage className={styles.inputElement} id={errorId}>
            {error}
          </ErrorMessage>
        ) : null}
      </VerticalStack>

      <DatePickerPopover
        calendarProps={calendarProps}
        calendarSize={calendarSize}
        dialogProps={dialogProps}
        locale={locale}
        pagination={pagination}
        parentDialog={parentDialog}
        placement={calendarPlacement}
        state={state}
        timeZone={usedTimeZone}
        triggerRef={triggerRef}
      />
    </VerticalStack>
  );
}
